import { useMemo } from 'react';
import * as ChartControlBarUI from '../ChartControlBarUI';
import { assertCondition } from '../../../../utils/types';
import ChartTypeSelector from '../../ChartTypeSelector';
import { useAppDispatch } from '../../../../redux/hooks';
import {
  FunnelModeKey,
  QueryType,
} from '../../../../utils/apollo/graphqlSchema';
import {
  setSingleDayLabelAction,
  toggleIsRelativeLabelsAction,
} from '../../../../redux/slices/analyses';
import SingleSelectMenu from '../../../Dropdown/SingleSelectMenu';
import { singleDayAllValue } from '../../../../utils/builder';
import { CHART_TYPES } from '../../constants/chartTypes';
import { ISingleSelectMenuOption } from '../../../Dropdown/SingleSelectMenu/types';
import { useChartContext } from '../../ChartContext';
import { freeze } from '@reduxjs/toolkit';

interface IProps {
  analysisId: string;
}

enum RelativeOptionValues {
  Relative = 'relative',
  Absolute = 'absolute',
}
const relativeOptions = freeze<ISingleSelectMenuOption[]>(
  [
    { text: 'Relative', value: RelativeOptionValues.Relative },
    { text: 'Absolute', value: RelativeOptionValues.Absolute },
  ],
  true,
);

const FunnelControlBar = ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();
  const {
    settings: { chartSettings },
    chartData,
  } = useChartContext();
  const { singleDayLabel, chartType, isFunnelRelativeLabels } = chartSettings;
  // narrow down chartData to IParsedChartDataFunnel
  assertCondition(chartData?.queryType === QueryType.Funnel);
  const changeSelectedDate = (selectedDate: string) =>
    dispatch(
      setSingleDayLabelAction({
        analysisId,
        singleDayLabel: selectedDate === singleDayAllValue ? '' : selectedDate,
      }),
    );

  const handleRelativeLabelsChange = (value: string) => {
    const isRelative = value === RelativeOptionValues.Relative;
    dispatch(toggleIsRelativeLabelsAction({ analysisId, value: isRelative }));
  };

  const datesOptions = useMemo<ISingleSelectMenuOption[]>(
    () => [
      {
        value: singleDayAllValue,
        text: 'Date - All',
      },
      ...chartData.dates
        .map(
          (date) =>
            ({
              value: date,
              text: date,
            }) satisfies ISingleSelectMenuOption,
        )
        .reverse(),
    ],
    [chartData.dates],
  );

  return (
    <ChartControlBarUI.ControlBarRow>
      <ChartTypeSelector />
      {/* comparative line chart always display all dates */}
      {chartType !== CHART_TYPES.COMPARATIVE_LINE_CHART && (
        <SingleSelectMenu
          options={datesOptions}
          value={singleDayLabel || singleDayAllValue}
          onChange={changeSelectedDate}
        />
      )}
      {/* only displayed for funnel conversion mode */}
      {chartData.mode === FunnelModeKey.Conversion && (
        <SingleSelectMenu
          options={relativeOptions}
          value={
            isFunnelRelativeLabels
              ? RelativeOptionValues.Relative
              : RelativeOptionValues.Absolute
          }
          onChange={handleRelativeLabelsChange}
        />
      )}
    </ChartControlBarUI.ControlBarRow>
  );
};

export default FunnelControlBar;
